<script setup>
import { HrCard, HrForm, HrPage } from '../primitives';

/**
 * Branded form page — a centered card layout with the app logo, a form body, optional actions, and a footer row.
 *
 * @status recommended
 * @group layout
 * @useWhen Login, signup, password reset, or other full-page unauthenticated form screens
 * @a11y The logo carries role="img" with an aria-label.
 */
defineOptions({ name: 'HrBrandedFormPage' });

defineEmits({
  /** Forwarded from the inner HrForm when the user submits. */
  submit: null,
});
</script>

<template>
  <hr-page class="hr-branded-form-card__page">
    <hr-card
      class="hr-branded-form-card"
      :legacy-props="{ class: 'hr-branded-form-card shadow-24' }"
    >
      <div class="hr-branded-form-card__logo">
        <div
          class="hr-branded-form-card__logo-img"
          role="img"
          aria-label="Hirasmus"
        />
      </div>

      <hr-form @submit="$emit('submit')">
        <div class="hr-branded-form-card__body">
          <!-- @slot Form content — fields, headings, and supporting text. -->
          <slot />
        </div>
        <div
          v-if="$slots.actions"
          class="hr-branded-form-card__actions"
        >
          <!-- @slot actions Action buttons stacked below the form body. -->
          <slot name="actions" />
        </div>
      </hr-form>

      <div
        v-if="$slots.footer"
        class="hr-branded-form-card__footer"
      >
        <!-- @slot footer Footer row below the card (e.g. language selector, legal links). -->
        <slot name="footer" />
      </div>
    </hr-card>
  </hr-page>
</template>

<style lang="scss" scoped>
// Class lands on q-page via HrPage's v-bind="$attrs"
.hr-branded-form-card__page {
  --hr-page-padding: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--app-background);
  color: var(--app-on-background);
}

.hr-branded-form-card {
  // Fill the viewport on small screens, then hold at a fixed cap so the card
  // stops widening on large screens — the centered page absorbs extra space as
  // margin (M3: a single content pane doesn't grow past the Medium size class).
  flex: 0 1 100%;
  max-width: var(--app-comp-branded-form-page-max-width);
  margin-bottom: 0;

  // M3: floating form card surface → Surface Container High
  :deep(.hr-card--elevated) {
    --hr-card-surface: var(--app-surface-container-high);
  }

  // Square corners only while the card actually spans the viewport (full-bleed);
  // once it floats with side margins it keeps its rounded corners. Breakpoint must
  // track --app-comp-branded-form-page-max-width (media queries can't read the var).
  @media (max-width: 480px) {
    border-radius: 0;
  }
}

.hr-branded-form-card__logo {
  display: flex;
  justify-content: center;
  padding: 24px 24px var(--app-comp-branded-form-page-logo-gap);
}

.hr-branded-form-card__logo-img {
  width: 33.33%;
  aspect-ratio: 800 / 216;
  background-image: var(--app-logo-url);
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.hr-branded-form-card__body {
  padding: 0 var(--app-comp-branded-form-page-content-padding) var(--app-comp-branded-form-page-sections-gap);
}

.hr-branded-form-card__actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--app-comp-branded-form-page-actions-gap);
  padding: 0 var(--app-comp-branded-form-page-content-padding) 16px;
}

.hr-branded-form-card__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 24px 12px;
  color: var(--app-on-surface-variant);
  font-size: 8px;
}
</style>
